<template>
  <div class="color-game">
    <h1 class="target-color">
      Pop the
      <span :style="{ color: targetColor.toLowerCase() }">{{ targetColor }}</span>
      balloons!
      <span class="balloon-icon" />
    </h1>

    <!-- Progress display with balloons always in target color -->
    <div class="progress-display">
      <div class="progress-balloons">
        <span
          v-for="n in totalTargetBalloonsToPop"
          :key="n"
          class="progress-balloon"
          :style="{
            color: targetColor.toLowerCase(),
            opacity: n <= poppedTargetBalloonsCount ? 1 : 0.3,
          }"
        >
          🎈
        </span>
      </div>
    </div>

    <div class="game-area">
      <div
        v-for="(balloon, index) in balloons"
        :key="index"
        :class="['balloon', { popped: balloon.popped }]"
        :style="{
          backgroundColor: balloon.color.toLowerCase(),
          bottom: `${balloon.y}px`,
          left: `${balloon.x}%`,
        }"
        @click="popBalloon(index)"
      >
        <div class="knot" />
      </div>
    </div>
    <div v-if="isGameComplete" class="level-complete">
      <p>🎉 Excellent!</p>
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin"; // update the path as needed

export default {
  mixins: [speechMixin],
  data() {
    return {
      colors: ["Red", "Blue", "Green", "Yellow", "Purple", "Orange"],
      targetColor: "",
      balloons: [],
      isGameComplete: false,
      gameInterval: null,
      totalTargetBalloonsToPop: 5, // Number of target balloons to pop before changing color
      poppedTargetBalloonsCount: 0, // Keeps track of popped target balloons
      totalNonTargetBalloons: 5, // Number of non-target balloons
    };
  },
  mounted() {
    this.startGame();
    this.startBalloons();
    // Initialize voices via the mixin
    this.setupVoices();
  },
  beforeUnmount() {
    clearInterval(this.gameInterval);
    window.speechSynthesis.cancel(); // Stop any ongoing speech synthesis
  },
  methods: {
    startGame() {
      if (this.colors.length === 0) {
        // All colors have been completed
        this.isGameComplete = true;
        this.speakOutLoud("Excellent!");
        setTimeout(() => {
          this.$router.push("/colors");
        }, 4000);
        return;
      }

      // Select a random color from the list
      this.targetColor = this.colors.splice(
        Math.floor(Math.random() * this.colors.length),
        1
      )[0];

      // Reset the popped target balloons count
      this.poppedTargetBalloonsCount = 0;

      // Generate balloons
      this.balloons = [];

      // Add target balloons
      for (let i = 0; i < this.totalTargetBalloonsToPop; i++) {
        this.balloons.push({
          color: this.targetColor,
          x: Math.random() * 90,
          y: -Math.random() * 500,
          popped: false,
        });
      }

      // Add non-target balloons
      for (let i = 0; i < this.totalNonTargetBalloons; i++) {
        const randomColor = this.getRandomNonTargetColor();
        this.balloons.push({
          color: randomColor,
          x: Math.random() * 90,
          y: -Math.random() * 500,
          popped: false,
        });
      }

      // Shuffle the balloons array to mix target and non-target balloons
      this.balloons = this.shuffleArray(this.balloons);

      // Speak the target color using the mixin
      this.speakOutLoud(`Pop the ${this.targetColor} balloons!`);
    },
    getRandomNonTargetColor() {
      // Return a random color from the colors list (excluding the target color)
      const allColors = ["Red", "Blue", "Green", "Yellow", "Purple", "Orange"];
      const nonTargetColors = allColors.filter(
        (color) => color !== this.targetColor
      );
      return nonTargetColors[
        Math.floor(Math.random() * nonTargetColors.length)
      ];
    },
    shuffleArray(array) {
      // Implementing Fisher-Yates shuffle
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    popBalloon(index) {
      const balloon = this.balloons[index];
      if (balloon.popped) return;

      if (balloon.color === this.targetColor) {
        // Correct balloon
        balloon.popped = true;
        this.poppedTargetBalloonsCount++;

        // Play pop sound
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );

        // Speak the target color using the mixin
        this.speakOutLoud(this.targetColor);

        if (this.poppedTargetBalloonsCount >= this.totalTargetBalloonsToPop) {
          // Move to the next color after a delay
          setTimeout(() => {
            this.startGame();
          }, 1000);
        }
      }
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    startBalloons() {
      this.gameInterval = setInterval(() => {
        this.balloons.forEach((balloon) => {
          if (!balloon.popped) {
            balloon.y += 1; // Move balloon upwards
            if (balloon.y > 600) {
              // Reset balloon position
              balloon.y = -100;
            }
          }
        });
      }, 16); // Approximately 60 frames per second
    },
  },
};
</script>

<style scoped>
.color-game {
  text-align: center;
}

.target-color {
  font-size: 2rem;
  margin-bottom: 10px;
}

.balloon-icon {
  font-size: 2rem;
  margin-left: 10px;
}

.progress-display {
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.progress-balloons {
  display: flex;
  justify-content: center;
  align-items: center;
}

.progress-balloon {
  font-size: 2.5rem;
  margin: 0 5px;
  transition: opacity 0.3s;
}

.game-area {
  position: relative;
  height: 500px;
  border: 2px solid #ccc;
  overflow: hidden;
  margin: 0 auto;
  width: 80%;
  margin-bottom: 30px;
  background: linear-gradient(to bottom, #87ceeb 0%, #ffffff 100%);
}

.balloon {
  position: absolute;
  width: 60px;
  height: 80px;
  border-radius: 30px 30px 40px 40px;
  cursor: pointer;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.3);
}

.balloon .knot {
  position: absolute;
  bottom: -5px;
  left: 27px;
  width: 6px;
  height: 10px;
  background-color: inherit;
  transform: rotate(45deg);
}

.balloon.popped {
  visibility: hidden;
}

.level-complete {
  font-size: 2rem;
  color: green;
}
</style>
